import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid2 as Grid,
  Typography,
  Divider,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { personSchema } from '../../schemas/validationSchemas';
import DateField from '../ui/DateField';
import { maskPhoneBR, cepMask } from '../../utils/masks';
import { lookupCep } from '../../services/viaCepService';
import personsService from '../../services/personsService';
import { isValidCpf } from '../../utils/cpfValidator';

/**
 * Formulário de Pessoa (Create/Update) com validação Zod.
 * Endereço estruturado alinhado com PlaceForm (CEP, Rua, Número, Complemento, Bairro, Cidade, UF).
 * Autocomplete de CEP via ViaCEP ao sair do campo.
 * Campo `address` legacy exibido somente em edição de registros antigos (readonly).
 */
export default function PersonForm({ open, onClose, onSubmit, data, loading = false }) {
  const isEdit = !!data;
  const [cepLoading, setCepLoading] = useState(false);
  // Aviso PROATIVO de CPF já cadastrado: o back bloqueia com 400 ("já existe pessoa com o CPF"),
  // mas o usuário só descobre ao submeter. Aqui avisamos no blur, ANTES do submit falhar.
  const [cpfDup, setCpfDup] = useState(null); // { name } ou null

  const checkCpfDup = async (value) => {
    const digits = String(value || '').replace(/\D/g, '');
    setCpfDup(null);
    if (digits.length !== 11 || !isValidCpf(digits)) return; // só checa CPF válido
    try {
      const results = await personsService.getAll({ search: digits });
      const match = (Array.isArray(results) ? results : []).find(
        (p) => String(p.cpf || '').replace(/\D/g, '') === digits && p.id !== data?.id,
      );
      if (match) setCpfDup({ name: match.name || 'pessoa sem nome' });
    } catch {
      /* silencioso: a checagem é conveniência; o back segue sendo a barreira */
    }
  };

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors, isValid, isDirty },
  } = useForm({
    resolver: zodResolver(personSchema),
    mode: 'onChange',
    reValidateMode: 'onBlur',
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      cpf: '',
      birthDate: '',
      address: '',
      addressZipCode: '',
      addressStreet: '',
      addressNumber: '',
      addressComplement: '',
      addressNeighborhood: '',
      addressCity: '',
      addressState: '',
      notes: '',
    },
  });

  useEffect(() => {
    if (open) {
      setCpfDup(null);
      if (data) {
        reset({
          name: data.name || '',
          email: data.email || '',
          phone: data.phone || '',
          cpf: data.cpf || '',
          birthDate: data.birthDate ? data.birthDate.split('T')[0] : '',
          address: data.address || '',
          addressZipCode: data.addressZipCode || '',
          addressStreet: data.addressStreet || '',
          addressNumber: data.addressNumber || '',
          addressComplement: data.addressComplement || '',
          addressNeighborhood: data.addressNeighborhood || '',
          addressCity: data.addressCity || '',
          addressState: data.addressState || '',
          notes: data.notes || '',
        });
      } else {
        reset({
          name: '',
          email: '',
          phone: '',
          cpf: '',
          birthDate: '',
          address: '',
          addressZipCode: '',
          addressStreet: '',
          addressNumber: '',
          addressComplement: '',
          addressNeighborhood: '',
          addressCity: '',
          addressState: '',
          notes: '',
        });
      }
    }
  }, [data, open, reset]);

  // Campos que o backend exige preenchidos (CreatePersonDto: [Required]). Não podem virar null —
  // se estiverem vazios, quem barra é a validação do próprio formulário, com mensagem melhor.
  const CAMPOS_OBRIGATORIOS = ['name', 'phone'];

  const onSubmitForm = (formData) => {
    const processed = {
      ...formData,
      // Backend espera telefone sem máscara (StringLength(10-11))
      phone: formData.phone?.replace(/\D/g, '') || '',
      // Backend espera CEP sem hífen (StringLength(8))
      addressZipCode: formData.addressZipCode?.replace(/\D/g, '') || '',
    };

    // Campo OPCIONAL vazio vai como null, nunca como "".
    //
    // Os defaultValues acima são todos '' e iam crus para a API. Para o backend, `null` significa
    // "não informado" e passa; `""` significa "informado, vazio" e vai para os validadores —
    // `[EmailAddress]` REPROVA string vazia. Resultado: quem cadastrava uma pessoa SEM e-mail
    // tomava 400 em todas as tentativas, e a mensagem que voltava não dizia qual campo era.
    // Caso real (2026-09-10): uma conta nova tentou 12x em 10 min e não cadastrou ninguém.
    // Genérico de propósito — tratar campo a campo só adia o próximo validador que rejeita "".
    for (const [chave, valor] of Object.entries(processed)) {
      if (CAMPOS_OBRIGATORIOS.includes(chave)) continue;
      if (typeof valor === 'string' && valor.trim() === '') processed[chave] = null;
    }

    onSubmit(processed);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleCepBlur = async (cepValue) => {
    const digits = (cepValue || '').replace(/\D/g, '');
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const result = await lookupCep(digits);
      if (result) {
        if (result.street) setValue('addressStreet', result.street, { shouldValidate: true });
        if (result.neighborhood) setValue('addressNeighborhood', result.neighborhood, { shouldValidate: true });
        if (result.city) setValue('addressCity', result.city, { shouldValidate: true });
        if (result.state) setValue('addressState', result.state, { shouldValidate: true });
      }
    } finally {
      setCepLoading(false);
    }
  };

  // Exibe o campo de endereço legado apenas em edição de registros antigos
  // (address preenchido, addressStreet vazio ~ nunca passou pelo novo form)
  const showLegacyAddress = isEdit && !!data?.address && !data?.addressStreet;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? 'Editar Pessoa' : 'Nova Pessoa'}
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmitForm)}>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2.5}>
            {/* Dados Pessoais */}
            <Box>
              <Typography variant="subtitle2" color="primary" gutterBottom fontWeight="bold">
                Dados Pessoais
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    autoFocus
                    label="Nome Completo"
                    fullWidth
                    {...register('name', {
                      onBlur: () => {
                        // Trigger validation on blur
                      }
                    })}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    required
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Email"
                    type="email"
                    fullWidth
                    {...register('email')}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="Telefone"
                        fullWidth
                        value={field.value}
                        onChange={(e) => field.onChange(maskPhoneBR(e.target.value))}
                        onBlur={field.onBlur}
                        error={!!errors.phone}
                        helperText={errors.phone?.message || '(11) 98765-4321'}
                        required
                        disabled={loading}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="CPF"
                    fullWidth
                    {...register('cpf', { onBlur: (e) => checkCpfDup(e.target.value) })}
                    error={!!errors.cpf}
                    helperText={
                      errors.cpf?.message ||
                      (cpfDup
                        ? `⚠️ Já existe uma pessoa com este CPF: ${cpfDup.name}`
                        : 'Somente números ou XXX.XXX.XXX-XX')
                    }
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="birthDate"
                    control={control}
                    render={({ field }) => (
                      <DateField
                        label="Data de Nascimento"
                        value={field.value || ''}
                        onChange={field.onChange}
                        error={!!errors.birthDate}
                        helperText={errors.birthDate?.message}
                        disabled={loading}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Endereço */}
            <Box>
              <Typography variant="subtitle2" color="primary" gutterBottom fontWeight="bold">
                Endereço
              </Typography>

              {/* Endereço legado — somente leitura para registros antigos */}
              {showLegacyAddress && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>Endereço anterior (migração):</strong> {data.address}
                  <br />
                  <Typography variant="caption">Preencha os campos abaixo para atualizar ao formato estruturado.</Typography>
                </Alert>
              )}

              <Grid container spacing={2}>
                {/* CEP — com máscara e autocomplete ViaCEP */}
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Controller
                    name="addressZipCode"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="CEP"
                        fullWidth
                        value={field.value}
                        onChange={(e) => field.onChange(cepMask(e.target.value))}
                        onBlur={(e) => {
                          field.onBlur();
                          handleCepBlur(e.target.value);
                        }}
                        error={!!errors.addressZipCode}
                        helperText={errors.addressZipCode?.message || '12345-678'}
                        disabled={loading}
                        InputProps={cepLoading ? {
                          endAdornment: (
                            <InputAdornment position="end">
                              <CircularProgress size={16} />
                            </InputAdornment>
                          ),
                        } : undefined}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 8 }}>
                  <TextField
                    label="Rua/Avenida"
                    fullWidth
                    {...register('addressStreet')}
                    error={!!errors.addressStreet}
                    helperText={errors.addressStreet?.message}
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <TextField
                    label="Número"
                    fullWidth
                    {...register('addressNumber')}
                    error={!!errors.addressNumber}
                    helperText={errors.addressNumber?.message}
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 6, sm: 5 }}>
                  <TextField
                    label="Complemento"
                    fullWidth
                    {...register('addressComplement')}
                    error={!!errors.addressComplement}
                    helperText={errors.addressComplement?.message}
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="Bairro"
                    fullWidth
                    {...register('addressNeighborhood')}
                    error={!!errors.addressNeighborhood}
                    helperText={errors.addressNeighborhood?.message}
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 8 }}>
                  <TextField
                    label="Cidade"
                    fullWidth
                    {...register('addressCity')}
                    error={!!errors.addressCity}
                    helperText={errors.addressCity?.message}
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <TextField
                    label="UF"
                    fullWidth
                    {...register('addressState')}
                    error={!!errors.addressState}
                    helperText={errors.addressState?.message || 'SP'}
                    disabled={loading}
                    inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Observações */}
            <Box>
              <Typography variant="subtitle2" color="primary" gutterBottom fontWeight="bold">
                Observações
              </Typography>
              <TextField
                label="Notas Adicionais"
                fullWidth
                multiline
                rows={3}
                {...register('notes')}
                error={!!errors.notes}
                helperText={errors.notes?.message || 'Informações relevantes sobre a pessoa'}
                disabled={loading}
              />
            </Box>

            {/* Alert de validação */}
            {Object.keys(errors).length > 0 && (
              <Alert severity="error">
                Por favor, corrija os erros acima antes de salvar.
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={loading || !isValid || (!isDirty && !isEdit)}>
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
